import React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Button, Divider } from '@mui/material';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';

export const stepTitle = "You're ready to publish!";
export const stepDescription =
  'Review your listing details below. You can still go back to make edits.';

const ListingPreview: React.FC = () => {
  // Mock data that would come from state management
  const mockCoverImage =
    'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=80&h=80&fit=crop';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: 848,
        width: '100%',
        margin: '0 auto',
      }}
    >
      {/* Property Details Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography
            variant='h6'
            sx={{
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '28px',
              color: '#111827',
            }}
          >
            Property Details
          </Typography>
          <Button
            variant='text'
            size='small'
            sx={{
              color: '#222222',
              fontSize: '14px',
              fontWeight: 500,
              textTransform: 'none',
              minWidth: 'auto',
              padding: '4px 8px',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            Edit
          </Button>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 4,
            mb: 3,
          }}
        >
          <Box>
            <Typography
              variant='body2'
              sx={{
                fontSize: '14px',
                lineHeight: '20px',
                color: '#6b7280',
                mb: 1,
              }}
            >
              Address
            </Typography>
            <Typography
              variant='body1'
              sx={{
                fontSize: '16px',
                lineHeight: '24px',
                color: '#111827',
                fontWeight: 500,
              }}
            >
              123 Oak Street, San Francisco, CA 94102
            </Typography>
          </Box>

          <Box>
            <Typography
              variant='body2'
              sx={{
                fontSize: '14px',
                lineHeight: '20px',
                color: '#6b7280',
                mb: 1,
              }}
            >
              Property Type
            </Typography>
            <Typography
              variant='body1'
              sx={{
                fontSize: '16px',
                lineHeight: '24px',
                color: '#111827',
                fontWeight: 500,
              }}
            >
              Single Family Home
            </Typography>
          </Box>

          <Box>
            <Typography
              variant='body2'
              sx={{
                fontSize: '14px',
                lineHeight: '20px',
                color: '#6b7280',
                mb: 1,
              }}
            >
              Beds & Baths
            </Typography>
            <Typography
              variant='body1'
              sx={{
                fontSize: '16px',
                lineHeight: '24px',
                color: '#111827',
                fontWeight: 500,
              }}
            >
              3 bed, 2 bath
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#e5e7eb' }} />
      </Box>

      {/* Listing Details Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography
            variant='h6'
            sx={{
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '28px',
              color: '#111827',
            }}
          >
            Listing Details
          </Typography>
          <Button
            variant='text'
            size='small'
            sx={{
              color: '#222222',
              fontSize: '14px',
              fontWeight: 500,
              textTransform: 'none',
              minWidth: 'auto',
              padding: '4px 8px',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            Edit
          </Button>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 4,
            mb: 3,
          }}
        >
          <Box>
            <Typography
              variant='body2'
              sx={{
                fontSize: '14px',
                lineHeight: '20px',
                color: '#6b7280',
                mb: 1,
              }}
            >
              Cover Photo
            </Typography>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 2,
                backgroundImage: `url(${mockCoverImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundColor: '#f3f4f6',
              }}
            />
          </Box>

          <Box>
            <Typography
              variant='body2'
              sx={{
                fontSize: '14px',
                lineHeight: '20px',
                color: '#6b7280',
                mb: 1,
              }}
            >
              Listing Title
            </Typography>
            <Typography
              variant='body1'
              sx={{
                fontSize: '16px',
                lineHeight: '24px',
                color: '#111827',
                fontWeight: 500,
              }}
            >
              Beautiful Modern Home in Prime Location
            </Typography>
          </Box>

          <Box>
            <Typography
              variant='body2'
              sx={{
                fontSize: '14px',
                lineHeight: '20px',
                color: '#6b7280',
                mb: 1,
              }}
            >
              Rent Amount
            </Typography>
            <Typography
              variant='h6'
              sx={{
                fontSize: '20px',
                lineHeight: '28px',
                color: '#111827',
                fontWeight: 500,
              }}
            >
              $4,200/month
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#e5e7eb' }} />
      </Box>

      {/* Screening Section */}
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography
            variant='h6'
            sx={{
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '28px',
              color: '#111827',
            }}
          >
            Screening
          </Typography>
          <Button
            variant='text'
            size='small'
            sx={{
              color: '#222222',
              fontSize: '14px',
              fontWeight: 500,
              textTransform: 'none',
              minWidth: 'auto',
              padding: '4px 8px',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            Edit
          </Button>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 4,
          }}
        >
          {/* Each item is a grid column */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: '#c6e9c7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 12, color: '#16a34a' }} />
            </Box>
            <Typography
              variant='body1'
              sx={{ fontSize: '16px', lineHeight: '24px', color: '#111827' }}
            >
              Credit Report
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: '#c6e9c7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 12, color: '#16a34a' }} />
            </Box>
            <Typography
              variant='body1'
              sx={{ fontSize: '16px', lineHeight: '24px', color: '#111827' }}
            >
              Background Check
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: '#c6e9c7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 12, color: '#16a34a' }} />
            </Box>
            <Typography
              variant='body1'
              sx={{ fontSize: '16px', lineHeight: '24px', color: '#111827' }}
            >
              Income Verification
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ListingPreview;
